import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/auth";

/* ============================================================
 /smartket — VALIDATION landing page for smartket.help.
 Two signup forms: founder side and marketer side. Collects
 emails + role + short "what you'd list / what you're good at."
 No account required — this is a public waitlist to prove
 demand before building the marketplace.
============================================================ */

export default function SmartKet() {
 const [stats, setStats] = useState({ founders: 0, marketers: 0 });

 // Standalone-brand tab title. Visitors landing from smartket.help see
 // "SmartKet" in their browser tab, not "Young Tongues" — reinforcing
 // that this is its own product.
 useEffect(() => {
  const prev = document.title;
  document.title = "SmartKet — Marketing that only pays on results";
  return () => { document.title = prev; };
 }, []);

 useEffect(() => {
  axios.get(`${API}/smartket/stats`).then(r => setStats(r.data)).catch(() => {});
 }, []);

 return (
  <div data-testid="smartket-page" className="min-h-screen bg-[#0d1410] text-white">
   {/* VERTICAL SPLIT HERO — two doors, two colors, one big tagline
       overlay. Left: business/founders (forest green — grow your
       business). Right: marketers (warm gold — sunlight, earnings).
       Both doors scroll down to the correct signup form. */}
   <section className="relative min-h-[85vh] md:min-h-[92vh] flex flex-col md:flex-row overflow-hidden">
    {/* LEFT DOOR — BUSINESS / FOUNDERS */}
    <a
     href="#signup-founder-anchor"
     data-testid="hero-founder-door"
     className="group relative flex-1 flex flex-col justify-end px-6 md:px-12 py-10 md:py-14 transition-all duration-500 hover:flex-[1.15]"
     style={{ background: "linear-gradient(180deg, #1a3a2a 0%, #2d5f3f 55%, #3d7a4f 100%)" }}
    >
     <div className="font-mono text-[10px] md:text-xs tracking-[0.5em] text-white/70 mb-3">
      ▸ FOR THE FOUNDER
     </div>
     <div className="font-display text-4xl md:text-6xl leading-none tracking-tight text-white mb-3">
      I HAVE A<br/>PRODUCT.
     </div>
     <p className="font-mono text-sm md:text-base text-white/85 leading-relaxed max-w-md mb-6">
      Got a business, a product, a platform — but no time or budget to market it? List it. Pay nothing till the money lands.
     </p>
     <div className="inline-flex items-center gap-2 self-start bg-white text-[#1a3a2a] font-display text-base md:text-lg tracking-widest px-5 py-3 shadow-brutal group-hover:bg-[#f2b06b] transition-colors">
      LIST YOUR PRODUCT ▸
     </div>
    </a>

    {/* MIDDLE OVERLAY — the big pun tagline. Sits over the seam. */}
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10">
     <div className="text-center px-4">
      <div className="font-mono text-[10px] md:text-xs tracking-[0.5em] text-white/80 mb-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
       SMARTKET
      </div>
      <h1 className="font-display text-5xl sm:text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tight text-white drop-shadow-[0_4px_16px_rgba(0,0,0,0.95)]">
       Money grows<br/>on <span className="text-[#f2c94c] italic">FREE</span>.
      </h1>
      <p className="mt-4 md:mt-6 font-mono text-xs md:text-base text-white/95 max-w-xl mx-auto leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]">
       Marketing help made easy. Pay only when the money lands. <span className="text-[#f2c94c]">Zero risk, all upside.</span>
      </p>
     </div>
    </div>

    {/* RIGHT DOOR — MARKETING MAGICIANS */}
    <a
     href="#signup-marketer-anchor"
     data-testid="hero-marketer-door"
     className="group relative flex-1 flex flex-col justify-end items-end text-right px-6 md:px-12 py-10 md:py-14 transition-all duration-500 hover:flex-[1.15]"
     style={{ background: "linear-gradient(180deg, #c98a3a 0%, #d4a34a 55%, #e6b85a 100%)" }}
    >
     <div className="font-mono text-[10px] md:text-xs tracking-[0.5em] text-[#3a2410] mb-3">
      FOR THE MARKETER ◂
     </div>
     <div className="font-display text-4xl md:text-6xl leading-none tracking-tight text-[#2a1a08] mb-3">
      I CAN<br/>MARKET.
     </div>
     <p className="font-mono text-sm md:text-base text-[#3a2410] leading-relaxed max-w-md mb-6">
      Sharp with growth, ads, content, or SEO? Pick products you believe in. Earn commission on every real sale you make happen.
     </p>
     <div className="inline-flex items-center gap-2 self-end bg-[#2a1a08] text-white font-display text-base md:text-lg tracking-widest px-5 py-3 shadow-brutal group-hover:bg-[#5a3410] transition-colors">
      ◂ EARN ON RESULTS
     </div>
    </a>
   </section>

   {/* LIVE STATS STRIP */}
   {(stats.founders > 0 || stats.marketers > 0) && (
    <div className="bg-[#0d1410] border-y border-white/10 py-3">
     <div className="max-w-5xl mx-auto px-4 flex justify-center gap-6 md:gap-10 font-mono text-xs md:text-sm text-white/70 flex-wrap">
      <div>🌱 <span className="text-[#3d7a4f] font-display text-lg mx-1">{stats.founders}</span> founders on the list</div>
      <div>✦ <span className="text-[#d4a34a] font-display text-lg mx-1">{stats.marketers}</span> marketers signed up</div>
     </div>
    </div>
   )}

   <div className="max-w-4xl mx-auto px-4 md:px-8 py-14 space-y-14">

    {/* THE DEAL — playful, warm, 3 steps */}
    <section>
     <div className="font-mono text-[10px] tracking-[0.4em] text-[#d4a34a] mb-3">
      HOW IT WORKS · IT'S DUMB SIMPLE
     </div>
     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[
       { n: "01", ico: "🌱", t: "FOUNDERS LIST",  d: "You've got a product but no budget to market it. List it here. Set your commission rate (20-50%). Zero monthly cost." },
       { n: "02", ico: "✦",  t: "MARKETERS PICK", d: "Freelance marketers browse listings and grab products they can actually move. They don't gamble — you don't gamble." },
       { n: "03", ico: "💰", t: "PAY ONLY ON WINS", d: "Every sale is verified via Stripe. Marketer earns their cut. You pay nothing until real money is in your wallet." },
      ].map(row => (
       <div key={row.n} className="border-2 border-white/10 bg-[#141a15] p-5 hover:border-[#d4a34a]/60 transition-colors">
        <div className="flex items-center justify-between mb-3">
         <div className="font-display text-3xl text-[#d4a34a]">{row.n}</div>
         <div className="text-2xl">{row.ico}</div>
        </div>
        <div className="font-display text-lg tracking-wider mb-2">{row.t}</div>
        <div className="font-mono text-xs text-white/75 leading-relaxed">{row.d}</div>
       </div>
      ))}
     </div>
    </section>

    {/* COMING SOON — teaser section so people SEE where it's going */}
    <section className="border-2 border-dashed border-[#d4a34a]/50 p-6 md:p-8 bg-[#1a1410]">
     <div className="font-mono text-[10px] tracking-[0.4em] text-[#f2c94c] mb-3">
      ▸ COMING SOON · WHAT YOU'RE GETTING ACCESS TO
     </div>
     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-black/40 border border-white/10 p-4">
       <div className="font-display text-xl md:text-2xl text-white mb-2">
        📋 The Product Board
       </div>
       <p className="font-mono text-xs text-white/75 leading-relaxed mb-3">
        A live feed of every product looking for marketers. See what's out there, what's hot, what's saturated. Founders see the competition. Marketers see the pool. Transparency both ways.
       </p>
       <Link to="/smartket/board" data-testid="board-link" className="inline-block bg-[#d4a34a] text-black font-mono text-[11px] tracking-widest px-3 py-2 hover:bg-white transition-colors">
        ▸ BROWSE THE BOARD (LIVE)
       </Link>
      </div>
      <div className="bg-black/40 border border-white/10 p-4">
       <div className="font-display text-xl md:text-2xl text-white mb-2">
        🎯 Market Score (AI)
       </div>
       <p className="font-mono text-xs text-white/75 leading-relaxed">
        Every product gets a Market Viability score (0-10) so marketers can pick winners and founders learn what actually sells. Not opinion — an actual model trained on real launches.
       </p>
      </div>
     </div>
     <div className="mt-4 font-mono text-[10px] text-white/50 tracking-widest">
      Waitlist members get first access · notify pricing lock-in · no launch spam
     </div>
    </section>

    {/* SIGNUP anchors — targeted by the two hero doors */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
     <div id="signup-founder-anchor">
      <SignupCard
       role="founder"
       heading="I HAVE A PRODUCT"
       sub="I want marketers to help me sell it"
       placeholder="Briefly: what's your product or platform? (URL is fine)"
       cta="LIST WHEN LIVE"
       testId="signup-founder"
      />
     </div>
     <div id="signup-marketer-anchor">
      <SignupCard
       role="marketer"
       heading="I CAN MARKET"
       sub="I want to earn commission on real sales"
       placeholder="Briefly: what's your experience or what you're good at?"
       cta="NOTIFY ME"
       testId="signup-marketer"
      />
     </div>
    </div>

    {/* TRUST SECTION */}
    <section className="border-2 border-yt-yellow bg-yt-surface p-6">
     <div className="font-display text-2xl tracking-widest text-yt-yellow mb-3">HOW WE MAKE IT FAIR</div>
     <ul className="space-y-3 font-mono text-sm text-white/85 leading-relaxed">
      <li className="flex gap-3"><span className="text-yt-yellow shrink-0">▸</span><span><strong className="text-yt-yellow">Stripe-verified revenue tracking.</strong> Founders link their Stripe account. Nobody can hide sales.</span></li>
      <li className="flex gap-3"><span className="text-yt-yellow shrink-0">▸</span><span><strong className="text-yt-yellow">Escrow deposits.</strong> Founders put money down when they list. If they cheat, the marketer gets paid from the escrow.</span></li>
      <li className="flex gap-3"><span className="text-yt-yellow shrink-0">▸</span><span><strong className="text-yt-yellow">Legally binding revenue-disclosure agreement.</strong> Every founder signs a real contract with audit rights.</span></li>
      <li className="flex gap-3"><span className="text-yt-yellow shrink-0">▸</span><span><strong className="text-yt-yellow">2-tier trust badges.</strong> Verified listings (Stripe-linked) attract better marketers. Self-reported listings still allowed but flagged clearly.</span></li>
     </ul>
    </section>

    {/* WHY THIS EXISTS */}
    <section className="border-l-4 border-yt-yellow pl-6">
     <div className="font-mono text-[10px] tracking-widest text-yt-yellow mb-2">WHY I'M BUILDING THIS</div>
     <div className="space-y-4 font-mono text-sm text-white/85 leading-relaxed italic max-w-2xl">
      <p>
       I have a traumatic brain injury and ADHD. I built a startup (<Link to="/" className="not-italic text-yt-yellow underline">Young Tongues</Link>) but I can't crack marketing. Agencies want $5K/month up front, no promises. I'm not throwing my kids' education money at a gamble.
      </p>
      <p>
       There has to be a way for founders like me to get help without betting the farm. And there has to be a way for hungry marketers to earn real money by picking winners — not just clip Amazon coupons.
      </p>
      <p className="not-italic text-yt-yellow">
       That's SmartKet. Founders only pay when it works. Marketers only get paid when they deliver. Everybody wins or nobody loses.
      </p>
      <p className="text-white/70 text-xs not-italic">— Paul (@RodeoRhymes), founder</p>
     </div>
    </section>

    <footer className="border-t border-yt-border pt-8 pb-8">
     <div className="font-display text-2xl sm:text-3xl text-yt-yellow tracking-wide text-center mb-4">
      FAIR PLAY, THE SMARTKET WAY.
     </div>
     <div className="font-mono text-xs text-yt-muted text-center">
      Reference implementation soon. Waitlist open now. From the team behind <Link to="/" className="text-yt-yellow underline">Young Tongues</Link> and <Link to="/openpersona" className="text-yt-yellow underline">OpenPersona</Link>.
     </div>
    </footer>
   </div>
  </div>
 );
}

function SignupCard({ role, heading, sub, placeholder, cta, testId }) {
 const [email, setEmail] = useState("");
 const [note, setNote] = useState("");
 const [busy, setBusy] = useState(false);
 const [done, setDone] = useState(false);
 const [err, setErr] = useState("");

 const submit = async (e) => {
  e.preventDefault();
  if (busy || !email.trim()) return;
  setBusy(true); setErr("");
  try {
   await axios.post(`${API}/smartket/signup`, {
    email: email.trim(),
    role,
    product_or_experience: note.trim() || null,
   });
   setDone(true);
  } catch (e2) {
   setErr(e2?.response?.data?.detail || "Signup failed — try again.");
  } finally { setBusy(false); }
 };

 if (done) {
  return (
   <div data-testid={`${testId}-done`} className="border-2 border-yt-yellow bg-yt-surface p-6">
    <div className="font-display text-2xl tracking-widest text-yt-yellow mb-2">{heading}</div>
    <div className="font-mono text-sm text-white/85 leading-relaxed">
     Locked in, brother. You'll be first in line when SmartKet opens. Watch your email.
    </div>
   </div>
  );
 }

 return (
  <form onSubmit={submit} data-testid={testId} className="border-2 border-yt-border bg-yt-surface p-6 hover:border-yt-yellow/60 transition-colors">
   <div className="font-display text-2xl tracking-widest text-white">{heading}</div>
   <div className="font-mono text-[10px] text-yt-muted tracking-widest mt-1">{sub}</div>
   <input
    data-testid={`${testId}-email`}
    type="email"
    required
    value={email}
    onChange={e => setEmail(e.target.value)}
    placeholder="you@email.com"
    className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow px-3 py-2 font-mono text-sm outline-none mt-4"
   />
   <textarea
    data-testid={`${testId}-note`}
    value={note}
    onChange={e => setNote(e.target.value)}
    rows={2}
    maxLength={800}
    placeholder={placeholder}
    className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow px-3 py-2 font-mono text-sm outline-none mt-2 resize-y"
   />
   {err && <div className="mt-2 font-mono text-xs text-yt-red">{err}</div>}
   <button
    data-testid={`${testId}-submit`}
    type="submit"
    disabled={busy || !email.trim()}
    className="mt-3 w-full bg-yt-yellow text-black font-display text-sm tracking-widest py-3 hover:bg-white disabled:opacity-40"
   >
    {busy ? "SUBMITTING…" : cta}
   </button>
  </form>
 );
}
